import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Building2, HardHat, ClipboardCheck, AlertTriangle, CheckCircle2,
  Users, TrendingUp, Download, Search, Filter, ArrowRight, Clock,
} from "lucide-react";
import QstLayout from "./QstLayout";
import QstAssistantWidget from "./QstAssistantWidget";
import { qstDashboardContractor, qstAcknowledge } from "./api";

const STATUS_COLOR = {
  "assigned":     { bg: "bg-blue-50",    fg: "text-blue-700",    ring: "ring-blue-200" },
  "in-progress":  { bg: "bg-amber-50",   fg: "text-amber-800",   ring: "ring-amber-200" },
  "overdue":      { bg: "bg-red-50",     fg: "text-red-700",     ring: "ring-red-200" },
  "completed":    { bg: "bg-emerald-50", fg: "text-emerald-700", ring: "ring-emerald-200" },
};

export default function QstContractorDashboard() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");

  useEffect(() => {
    document.title = "Contractor Dashboard — QST";
    qstDashboardContractor().then(setData).catch(() => {});
  }, []);

  async function acknowledge(assignment_id) {
    setBusy(true);
    try {
      await qstAcknowledge(assignment_id, "Demo Foreman");
      const r = await qstDashboardContractor();
      setData(r);
    } finally { setBusy(false); }
  }

  function exportCsv() {
    if (!data) return;
    const rows = [
      ["Project", "Lesson", "Trade", "Assigned To", "Due", "Status", "Acknowledged", "Total Workers"],
      ...data.assignments.map((a) => [
        (data.projects.find((p) => p.id === a.project) || {}).name || a.project,
        a.lesson_meta.title, a.trade, a.assigned_to, a.due_date,
        a.status, a.acknowledged, a.total_workers,
      ]),
    ];
    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `qst-training-report-${Date.now()}.csv`; a.click();
    URL.revokeObjectURL(url);
  }

  if (!data) return (
    <QstLayout>
      <div className="max-w-6xl mx-auto px-6 py-20 text-center text-slate-500">Loading dashboard…</div>
    </QstLayout>
  );

  const filteredAssignments = data.assignments
    .filter((a) => filter === "all" || a.status === filter)
    .filter((a) => !q || a.lesson_meta.title.toLowerCase().includes(q.toLowerCase()) || a.assigned_to.toLowerCase().includes(q.toLowerCase()));

  return (
    <QstLayout>
      <QstAssistantWidget context={{ page: "dashboard" }} />

      <section className="bg-[#0B2545] text-white py-10 border-b border-slate-800" data-testid="qst-dashboard">
        <div className="max-w-7xl mx-auto px-6 flex items-start justify-between flex-wrap gap-4">
          <div>
            <div className="text-xs uppercase tracking-widest text-[#F97316] font-bold">Contractor Dashboard</div>
            <h1 className="mt-2 text-3xl sm:text-4xl font-black tracking-tight">Training across every crew and project.</h1>
            <p className="mt-2 text-sm text-slate-300 max-w-2xl">
              Live acknowledgment rates, overdue assignments and exportable participation records.
              {data.note && <span className="block text-[11px] text-amber-300 mt-1">{data.note}</span>}
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button onClick={exportCsv}
              className="inline-flex items-center gap-1.5 rounded-md bg-white/10 hover:bg-white/20 border border-white/20 text-white px-4 py-2 text-sm font-bold"
              data-testid="qst-dashboard-export">
              <Download className="w-4 h-4" /> Export CSV
            </button>
            <Link to="/qst/library"
              className="inline-flex items-center gap-1.5 rounded-md bg-[#F97316] hover:bg-[#EA580C] text-white px-4 py-2 text-sm font-bold">
              Assign a lesson <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* KPIs */}
      <section className="max-w-7xl mx-auto px-6 -mt-6">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 bg-white rounded-2xl border border-slate-200 shadow-md p-5"
             data-testid="qst-dashboard-kpis">
          <Kpi Icon={Building2}     label="Active projects" value={data.kpis.projects_active} accent="#0B2545" />
          <Kpi Icon={ClipboardCheck} label="Assignments"    value={data.kpis.assignments_total} accent="#F97316" />
          <Kpi Icon={AlertTriangle} label="Overdue"         value={data.kpis.assignments_overdue} accent="#DC2626" />
          <Kpi Icon={CheckCircle2}  label="Completed"       value={data.kpis.assignments_completed} accent="#059669" />
          <Kpi Icon={TrendingUp}    label="Acknowledgment"  value={`${data.kpis.acknowledgment_rate}%`} accent="#7C3AED" />
          <Kpi Icon={Users}         label="Worker reach"    value={data.kpis.worker_reach} accent="#0891B2" />
        </div>
      </section>

      {/* Projects */}
      <section className="max-w-7xl mx-auto px-6 mt-10" data-testid="qst-dashboard-projects">
        <h2 className="text-xl font-bold text-[#0B2545]">Projects</h2>
        <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
          {data.projects.map((p) => (
            <div key={p.id} className="rounded-xl border border-slate-200 bg-white p-5"
                 data-testid={`qst-project-${p.id}`}>
              <div className="text-base font-bold text-[#0B2545]">{p.name}</div>
              <div className="mt-1 text-xs text-slate-500">{p.location} · {p.phase}</div>
              <div className="mt-4 flex items-center gap-4 text-xs">
                <span className="inline-flex items-center gap-1 text-slate-700"><HardHat className="w-3.5 h-3.5 text-[#F97316]" /> {p.crews} crews</span>
                <span className="inline-flex items-center gap-1 text-slate-700"><Users className="w-3.5 h-3.5 text-[#F97316]" /> {p.workers} workers</span>
              </div>
              <div className="mt-3">
                <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
                  <div className="h-full bg-gradient-to-r from-[#F97316] to-[#EA580C]" style={{ width: `${p.progress}%` }} />
                </div>
                <div className="mt-1 flex justify-between text-[11px] text-slate-500">
                  <span>{p.progress}% complete</span>
                  <span>Target {p.target_end}</span>
                </div>
              </div>
              <div className="mt-3 text-xs text-slate-600">Supervisor · <strong>{p.supervisor}</strong></div>
            </div>
          ))}
        </div>
      </section>

      {/* Assignments */}
      <section className="max-w-7xl mx-auto px-6 mt-10 pb-16" data-testid="qst-dashboard-assignments">
        <div className="flex flex-wrap items-baseline justify-between gap-3">
          <h2 className="text-xl font-bold text-[#0B2545]">Training assignments</h2>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="inline-flex items-center gap-2 bg-white border border-slate-300 rounded-md px-3 py-1.5">
              <Search className="w-3.5 h-3.5 text-slate-500" />
              <input value={q} onChange={(e) => setQ(e.target.value)}
                placeholder="Search by lesson or crew…"
                className="outline-none text-sm w-56" data-testid="qst-dashboard-search" />
            </div>
            <select value={filter} onChange={(e) => setFilter(e.target.value)}
              className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm font-semibold"
              data-testid="qst-dashboard-filter">
              <option value="all">All statuses</option>
              <option value="assigned">Assigned</option>
              <option value="in-progress">In progress</option>
              <option value="overdue">Overdue</option>
              <option value="completed">Completed</option>
            </select>
          </div>
        </div>

        <div className="mt-4 rounded-xl border border-slate-200 bg-white overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr className="text-left text-xs font-bold uppercase tracking-widest text-slate-600">
                <th className="px-4 py-3">Lesson</th>
                <th className="px-4 py-3 hidden md:table-cell">Project</th>
                <th className="px-4 py-3 hidden lg:table-cell">Crew</th>
                <th className="px-4 py-3">Due</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Progress</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredAssignments.map((a) => {
                const s = STATUS_COLOR[a.status] || STATUS_COLOR.assigned;
                const project = data.projects.find((p) => p.id === a.project);
                return (
                  <tr key={a.id} className="border-b border-slate-100 last:border-0 hover:bg-slate-50"
                      data-testid={`qst-assignment-${a.id}`}>
                    <td className="px-4 py-3">
                      <Link to={`/qst/lessons/${a.lesson_meta.slug}`}
                        className="font-bold text-[#0B2545] hover:underline">
                        {a.lesson_meta.title}
                      </Link>
                      <div className="text-[11px] text-slate-500 flex items-center gap-1 mt-0.5">
                        <Clock className="w-3 h-3" /> {a.lesson_meta.estimated_minutes} min · v{a.lesson_meta.version}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-slate-700 hidden md:table-cell">
                      {project?.name || a.project}
                    </td>
                    <td className="px-4 py-3 text-slate-700 hidden lg:table-cell">{a.assigned_to}</td>
                    <td className="px-4 py-3 text-slate-700">{a.due_date}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-widest ring-1 ${s.bg} ${s.fg} ${s.ring}`}>
                        {a.status.replace('-', ' ')}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div className="flex-1 min-w-[60px] h-1.5 rounded-full bg-slate-100 overflow-hidden">
                          <div className="h-full bg-[#F97316]"
                               style={{ width: `${Math.round((a.acknowledged / a.total_workers) * 100)}%` }} />
                        </div>
                        <span className="text-[11px] text-slate-600 font-semibold">
                          {a.acknowledged}/{a.total_workers}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button onClick={() => acknowledge(a.id)}
                        disabled={busy || a.status === "completed"}
                        className="rounded-md bg-[#F97316] hover:bg-[#EA580C] disabled:opacity-30 text-white text-xs font-bold px-3 py-1.5"
                        data-testid={`qst-ack-${a.id}`}>
                        Acknowledge
                      </button>
                    </td>
                  </tr>
                );
              })}
              {filteredAssignments.length === 0 && (
                <tr><td colSpan={7} className="px-4 py-10 text-center text-sm text-slate-500">No assignments match this filter.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </section>
    </QstLayout>
  );
}

function Kpi({ Icon, label, value, accent }) {
  return (
    <div className="flex flex-col gap-1">
      <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 flex items-center gap-1">
        <Icon className="w-3.5 h-3.5" style={{ color: accent }} /> {label}
      </div>
      <div className="text-2xl font-black text-[#0B2545]">{value}</div>
    </div>
  );
}
